import React, { useState, useEffect, useCallback } from "react";
import {
	InputLabel,
	MenuItem,
	FormControl,
	Select,
	FormHelperText,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
	createSession,
	readValues,
	setValue,
	keepSessionAlive,
} from "../functions/excelFunctions";
import { SelectValues } from "../data/SelectValues.js";
import { Typography } from "@mui/material";
import {
	LineChart,
	Line,
	CartesianGrid,
	XAxis,
	YAxis,
	Legend,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import "../css/TimeSeriesForecasting.css";
import { floatStringSorting } from "../functions/sortingFunctions";

function TimeSeriesForecasting() {
	const [sessionInfo, setSessionInfo] = useState(null);
	const [tableHeaders, setTableHeaders] = useState([]);
	const [tableRows, setTableRows] = useState([]);
	const [datagridColumns, setDatagridColumns] = useState([]);
	const [datagridRows, setDatagridRows] = useState([]);
	const [chart, setChart] = useState("");
	const sheet = "Time series forecasting";
	const functionCallDelay = 100; //ms
	const [selectedLocalAuthorities, setSelectedLocalAuthorities] = useState(
		[]
	);
	const [latestTableHeaders, setLatestTableHeaders] = useState([]);
	const [latestTableRows, setLatestTableRows] = useState([]);
	const [selectComponentClosed, setSelectComponentClosed] = useState(true);
	const chartLineColors = [
		"#006252",
		"#1f77b4",
		"#ff7f0e",
		"#2ca02c",
		"#d62728",
		"#9467bd",
		"#8c564b",
		"#e377c2",
		"#7f7f7f",
		"#bcbd22",
		"#17becf",
		"#5254a3",
		"#bd9e39",
		"#ad494a",
		"#4e79a7",
		"#f28e2c",
		"#e15759",
		"#76b7b2",
		"#59a14f",
		"#edc948",
		"#ed756b",
		"#845d8c",
		"#6b4c9a",
		"#9e7a44",
	];

	const [inputFilled, setInputFilled] = useState(new Array(3).fill(false));

	const getData = useCallback(() => {
		if (sessionInfo) {
			setTimeout(() => {
				readValues(
					sessionInfo,
					setLatestTableHeaders,
					sheet,
					"B13:D13"
				);
			}, 1 * functionCallDelay);

			setTimeout(() => {
				readValues(sessionInfo, setLatestTableRows, sheet, "B14:D23");
			}, 2 * functionCallDelay);
		}
	}, [sessionInfo, setLatestTableHeaders, setLatestTableRows]);

	useEffect(() => {
		setTableRows((tableRows) => [...tableRows, latestTableRows]);
	}, [latestTableRows]);

	useEffect(() => {
		setTableHeaders((tableHeaders) => [
			...tableHeaders,
			latestTableHeaders,
		]);
	}, [latestTableHeaders]);

	useEffect(() => {
		createSession(setSessionInfo);
	}, []);

	useEffect(() => {
		getData();
	}, [getData]);

	function findNearestTen(number) {
		var nearestLowerTen = Math.floor(number / 10) * 10;
		var nearestUpperTen = nearestLowerTen + 10;
		if (number - nearestLowerTen < nearestUpperTen - number) {
			return nearestLowerTen;
		} else {
			return nearestUpperTen;
		}
	}

	function getCustomColor(index) {
		const indexColor = Math.floor((index - 1) / 2);
		return chartLineColors[indexColor];
	}

	useEffect(() => {
		if (
			tableHeaders.length &&
			tableRows.length &&
			selectedLocalAuthorities.length
		) {
			const currentDatagridColumns = [];
			for (let i = 0; i < tableHeaders.length; i++) {
				if (typeof selectedLocalAuthorities[i] !== "undefined") {
					if (i === 0) {
						currentDatagridColumns.push({
							field: "column0",
							headerName: tableHeaders[i][0][0],
							width: 100,
						});
						currentDatagridColumns.push({
							field: "column1",
							headerName: tableHeaders[i][0][1],
							minWidth: 300,
							flex: 1,
							sortComparator: floatStringSorting,
						});
						currentDatagridColumns.push({
							field: "column2",
							headerName: tableHeaders[i][0][2].replace(
								"(LA)",
								"(" + selectedLocalAuthorities[i] + ")"
							),
							minWidth: 300,
							flex: 1,
							sortComparator: floatStringSorting,
						});
					} else {
						currentDatagridColumns.push({
							field: "column" + (i + 2),
							headerName: tableHeaders[i][0][2].replace(
								"(LA)",
								"(" + selectedLocalAuthorities[i] + ")"
							),
							minWidth: 300,
							flex: 1,
							sortComparator: floatStringSorting,
						});
					}
				}
			}
			setDatagridColumns(currentDatagridColumns);

			const currentDatagridRows = [];
			for (let i = 0; i < tableRows[0].length; i++) {
				let newRow = { id: i };
				for (let j = 0; j < tableRows.length; j++) {
					if (j === 0) {
						for (let k = 0; k < tableRows[j][i].length; k++) {
							newRow["column" + k] = tableRows[j][i][k];
						}
					} else {
						newRow["column" + (j + 2)] = tableRows[j][i][2];
					}
				}
				currentDatagridRows.push(newRow);
			}
			setDatagridRows(currentDatagridRows);

			// setting up the data for the chart
			const chartData = currentDatagridRows.map(
				({ id, ...rest }) => rest
			);

			// find position of current year
			let currentYearPosition;
			for (
				currentYearPosition = 0;
				currentYearPosition < chartData.length;
				currentYearPosition++
			) {
				if (
					Object.values(chartData[currentYearPosition]).includes(
						"2022"
					)
				) {
					break;
				}
			}

			let chartMin = Infinity;
			let chartMax = -Infinity;

			for (let i = 0; i < chartData.length; i++) {
				// eslint-disable-next-line
				Object.keys(chartData[i]).forEach((key, index) => {
					if (index === 0) {
						chartData[i][currentDatagridColumns[index].headerName] =
							chartData[i][key];
					} else {
						try {
							chartData[i][
								currentDatagridColumns[index].headerName
							] =
								i <= currentYearPosition
									? chartData[i][key]
									: null;
							chartData[i][
								"Forecasted " +
									currentDatagridColumns[index].headerName
							] =
								i >= currentYearPosition
									? chartData[i][key]
									: null;

							chartMin =
								parseFloat(chartData[i][key]) < chartMin
									? parseFloat(chartData[i][key])
									: chartMin;
							chartMax =
								parseFloat(chartData[i][key]) > chartMax
									? parseFloat(chartData[i][key])
									: chartMax;
						} catch (e) {
							console.error(e);
						}
					}

					delete chartData[i][key];
				});
			}

			const headers = Object.keys(chartData[0]);
			setChart(
				<div className="chart-container">
					<ResponsiveContainer width="100%" height={350}>
						<LineChart data={chartData}>
							{headers.map((_, index) => {
								if (index === 0) {
									return null;
								} else {
									if (
										(index === 1 || index === 2) &&
										headers.includes(
											"CLA per 10,000 population aged under 18 (Wales)"
										)
									) {
										return null;
									} else {
										return (
											<Line
												type="linear"
												dataKey={headers[index]}
												fill={getCustomColor(index)}
												stroke={getCustomColor(index)}
												name={
													headers[index].match(
														/\(([^)]+)\)[^(]*$/
													)[1]
												}
												legendType={
													index % 2 ? "line" : "none"
												}
												strokeDasharray={
													index % 2 === 0
														? "8 8"
														: null
												}
												key={"linechart-" + index}
											/>
										);
									}
								}
							})}
							<CartesianGrid
								stroke="#ccc"
								horizontal={false}
								vertical={false}
							/>
							<XAxis
								dataKey={headers[0]}
								tick={{ dy: 10 }}
								padding={{ left: 20, right: 20 }}
							/>
							<YAxis
								axisLine={false}
								tick={{ dx: -10 }}
								scale="linear"
								domain={[
									findNearestTen(chartMin) - 10,
									findNearestTen(chartMax) + 10,
								]}
							/>
							<Tooltip />
							<Legend
								formatter={(name, _, index) => {
									if (index % 2 !== 0) {
										return "";
									}
									return name;
								}}
							/>
						</LineChart>
					</ResponsiveContainer>
					<div id="chart-custom-legend">
						<div
							id="legend-a"
							style={{ display: "flex", margin: "auto" }}
						>
							<div
								style={{
									borderTop: "2px solid #676767",
									width: 50,
									margin: "10px",
								}}
							/>
							CLA
						</div>
						<div
							id="legend-b"
							style={{ display: "flex", margin: "auto" }}
						>
							<div
								style={{
									borderTop: "2px dashed #676767",
									width: 50,
									margin: "10px",
									fontSize: "0.8rem",
								}}
							/>
							Forecasted CLA
						</div>
					</div>{" "}
					<div id="chart-custom-legend-short">
						<div
							id="legend-a"
							style={{ display: "flex", margin: "auto" }}
						>
							<div
								style={{
									borderTop: "2px solid #676767",
									width: 50,
									margin: "10px",
								}}
							/>
							CLA
						</div>
						<div
							id="legend-b"
							style={{ display: "flex", margin: "auto" }}
						>
							<div
								style={{
									borderTop: "2px dashed #676767",
									width: 50,
									margin: "10px",
									fontSize: "0.8rem",
								}}
							/>
							Forecasted CLA
						</div>
					</div>
				</div>
			);
		}
		// eslint-disable-next-line
	}, [tableHeaders, tableRows]);

	useEffect(() => {
		if (selectComponentClosed) {
			setTableHeaders([]);
			setTableRows([]);
			for (let i = 0; i < selectedLocalAuthorities.length; i++) {
				setTimeout(() => {
					setValue(
						sessionInfo,
						sheet,
						2,
						1,
						selectedLocalAuthorities[i]
					);
					getData();
				}, i * 3 * functionCallDelay);
			}
		}
		// eslint-disable-next-line
	}, [selectedLocalAuthorities, selectComponentClosed]);

	setInterval(() => {
		sessionInfo && keepSessionAlive(sessionInfo, sheet);
	}, 5 * 60 * 1000); // Send the request every 5 minutes
	return (
		<div className="page-content" id="timeseriesforecasting-body">
			{sessionInfo ? (
				<div>
					<Typography variant="h4" style={{ marginBottom: 30 }}>
						Time Series Forecasting
					</Typography>
					<Typography variant="body1" style={{ marginBottom: 30 }}>
						Use this dashboard to generate forecasts through 2026
						for key indicators using historical data. The graph
						shows how the forecast for the selected local authority
						compares to Wales-wide forecasts as a benchmark.
					</Typography>
					<div>
						<div className="multiple-selects-container">
							<div className="select-container">
								<div className="select-text">
									Select a Local Authority
								</div>

								<FormControl variant="standard">
									<InputLabel id="local-authority-select-label">
										Local Authority
									</InputLabel>
									<Select
										labelId="local-authority-select-label"
										id="local-authority-select"
										multiple
										value={selectedLocalAuthorities}
										label="Local Authority"
										onChange={(e) => {
											e.target.value !==
												selectedLocalAuthorities &&
												setSelectedLocalAuthorities(
													e.target.value
												);
											let inputFilledTemp = [
												...inputFilled,
											];
											if (e.target.value.length > 0) {
												inputFilledTemp[0] = true;
											} else {
												inputFilledTemp[0] = false;
											}
											setInputFilled(inputFilledTemp);
										}}
										onOpen={() =>
											setSelectComponentClosed(false)
										}
										onClose={() =>
											setSelectComponentClosed(true)
										}
										renderValue={(selected) =>
											selected.join(", ")
										}
									>
										{SelectValues.TimeSeriesForecasting.LocalAuthorities.map(
											(LocalAuthority) => (
												<MenuItem
													key={LocalAuthority}
													value={LocalAuthority}
												>
													<Checkbox
														checked={
															selectedLocalAuthorities.indexOf(
																LocalAuthority
															) > -1
														}
													/>
													<ListItemText
														primary={LocalAuthority}
													/>
												</MenuItem>
											)
										)}
									</Select>
									<FormHelperText>
										Forecasts can be generated for specific
										local authorities or Wales-wide.
									</FormHelperText>
								</FormControl>
							</div>
							<div className="select-container">
								<div className="select-text">
									Select a forecasting method
								</div>
								<FormControl variant="standard">
									<InputLabel id="forecasting-method-select-label">
										Forecasting Method
									</InputLabel>
									<Select
										labelId="forecasting-method-select-label"
										id="forecasting-method-select"
										onChange={(e) => {
											setValue(
												sessionInfo,
												sheet,
												5,
												1,
												e.target.value
											);
											const previousSelectedLocalAuthorities =
												selectedLocalAuthorities;
											setSelectedLocalAuthorities([]);
											setSelectedLocalAuthorities([
												...previousSelectedLocalAuthorities,
											]);
											if (!inputFilled[1]) {
												let inputFilledTemp = [
													...inputFilled,
												];
												inputFilledTemp[1] = true;
												setInputFilled(inputFilledTemp);
											}
										}}
										label="Forecasting Method"
										defaultValue={""}
									>
										{SelectValues.TimeSeriesForecasting.ForecastingMethods.map(
											(option) => (
												<MenuItem
													key={option}
													value={option}
												>
													{option}
												</MenuItem>
											)
										)}
									</Select>
									<FormHelperText>
										You can choose between two different
										forecasting methods, model 1 (which
										emphasises long-term trends) or model 2
										(which emphasises short-term trends). A
										more technical explanation is provided
										in the footnote at the bottom of the
										page.
									</FormHelperText>
								</FormControl>
							</div>
							<div className="select-container">
								<div className="select-text">
									Select an indicator
								</div>
								<FormControl variant="standard">
									<InputLabel id="indicator-select-label">
										Indicator
									</InputLabel>
									<Select
										labelId="indicator-select-label"
										id="indicator-select"
										onChange={(e) => {
											setValue(
												sessionInfo,
												sheet,
												8,
												1,
												e.target.value
											);
											const previousSelectedLocalAuthorities =
												selectedLocalAuthorities;
											setSelectedLocalAuthorities([]);
											setSelectedLocalAuthorities([
												...previousSelectedLocalAuthorities,
											]);
											if (!inputFilled[2]) {
												let inputFilledTemp = [
													...inputFilled,
												];
												inputFilledTemp[2] = true;
												setInputFilled(inputFilledTemp);
											}
										}}
										label="Indicator"
										defaultValue={""}
									>
										{SelectValues.TimeSeriesForecasting.Indicators.map(
											(option) => (
												<MenuItem
													key={option}
													value={option}
												>
													{option}
												</MenuItem>
											)
										)}
									</Select>

									<FormHelperText>
										Forecasts can be generated for 1) the
										share of CLA relative to the under-18
										population, 2) the share of CLA by age
										group, or 3) the share of CLA by
										placement.
									</FormHelperText>
								</FormControl>
							</div>
						</div>
						{inputFilled.every((element) => element === true) ? (
							datagridColumns.length && datagridRows.length ? (
								<div className="outputs-data-container">
									{chart}
									<div
										className="table-container"
										style={{ width: "100%" }}
									>
										<DataGrid
											rows={datagridRows}
											columns={datagridColumns}
											disableRowSelectionOnClick
											disableColumnMenu
											disableColumnSelector
											disableVirtualization
											hideFooter={true}
											autoHeight={true}
											density={"compact"}
										/>
									</div>
									<div className="table-footer-text">
										Notes on forecasting methods:
										<br />
										<b>Model 1:</b> Theta model (combination
										of linear regression models that
										integrate simple exponential smoothing
										with the average change in the
										forecasted variable over time)
										<br />
										<b>Model 2:</b> Holt's linear trend
										model (extension of simple exponential
										smoothing based on two weighted
										averages, one for levels and one for
										trends, with more recent observations
										weighed more heavily)
									</div>
								</div>
							) : (
								<div className="loader" />
							)
						) : (
							<div className="no-data-text">
								Start entering data to get the project outputs
							</div>
						)}
					</div>
				</div>
			) : (
				<div className="loader" />
			)}
		</div>
	);
}
export default TimeSeriesForecasting;
